import {Navigate} from 'react-router'
import moment from 'moment-timezone'
import {fromMaybe} from '@freckle/maybe'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {
  useAsync,
  useAsyncOnceDefined
} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {useCourseMemberships} from '@freckle/educator-entities/ts/common/hooks/use-course-memberships'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import {
  fetchRlDomains,
  fetchRlStandardsWithDomain
} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {
  fetchRlStandardSetForSchool,
  fetchRlStandardSets
} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import Notification from '@freckle/school/ts/common/helpers/notification'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {useNumFromUrlParamKey, useQueryParamAsNum} from '@freckle/school/ts/hooks/use-params'
import {useStudents} from '@freckle/school/ts/hooks/use-students'
import {Assessments} from '@freckle/school/ts/pages/assessments/components/assessments'
import type {MathSchoolAssessmentAssignmentT} from '@freckle/school/ts/pages/assessments/models/math-school-assessment-assignment'
import {fetchMathSchoolAssessmentAssignment} from '@freckle/school/ts/pages/assessments/models/math-school-assessment-assignment'
import {getAllStudentsBySchool} from './utils'

export default function AssessmentsHandler() {
  const schoolId = useNumFromUrlParamKey('pathSchoolId')
  const year = useQueryParamAsNum('year')

  const courses = useCourses()
  const courseMemberships = useCourseMemberships()
  const schools = useSchools()
  const students = useStudents()
  const teachers = useTeachersAsASchoolAdmin()

  return (
    <WithResources
      resources={{
        courses,
        courseMemberships,
        schools,
        students,
        teachers
      }}
      render={({courses, courseMemberships, schools, students, teachers}) => {
        if (year === null || schoolId === null) {
          if (schools[0] !== undefined) {
            // Nothing selected and we have at least one school, select the first one.
            return (
              <Navigate
                to={Routes.schools.assessments(
                  schools[0].id,
                  fromMaybe(() => moment().year(), year)
                )}
                replace
              />
            )
          } else {
            // Nothing selected and there are no schools. This page requres a school so navigate away.
            Notification.displayNotification('default', 'No schools to display.')
            return <Navigate to={Routes.dashboard()} replace />
          }
        }

        const currentSchool = schools.find(school => school.id === schoolId)

        if (currentSchool === undefined) {
          // We were given an ID that we don't know about, try again, with nothing selected.
          Notification.displayErrorNotification('School not found, please choose another.')
          return <Navigate to={Routes.assessments()} replace />
        }

        return (
          <Inner
            currentSchool={currentSchool}
            courses={courses}
            courseMemberships={courseMemberships}
            schools={schools}
            students={students}
            teachers={teachers}
            year={year}
          />
        )
      }}
    />
  )
}

type InnerPropsT = {
  courses: CourseAttrs[]
  courseMemberships: CourseMembershipAttrs[]
  schools: SchoolAttrs[]
  currentSchool: SchoolAttrs
  students: StudentAttrs[]
  teachers: BaseTeacherAttrs[]
  year: number
}

function Inner(props: InnerPropsT) {
  const {currentSchool, year} = props
  const schoolStandardSet = useAsync(
    () => fetchRlStandardSetForSchool(currentSchool.id),
    'fetchRlStandardSetForSchool',
    [currentSchool.id]
  )
  const mathStandardSet = useAsyncOnceDefined(
    ({schoolStandardSet}) =>
      fetchRlStandardSets({'content-area': 'math', 'standard-set': schoolStandardSet.id}).then(
        standardSets => standardSets[0] ?? null
      ),
    'fetchRlStandardSet',
    ['math', schoolStandardSet.data],
    {schoolStandardSet}
  )
  const assessments = useAsync(
    () => fetchMathSchoolAssessmentAssignment(currentSchool.id, year),
    'fetchMathSchoolAssessmentAssignment',
    [currentSchool.id, year]
  )

  return (
    <WithResources
      resources={{
        mathStandardSet,
        assessments
      }}
      render={({mathStandardSet, assessments}) => {
        if (mathStandardSet === null) {
          return <Navigate to={Routes.assessments()} replace />
        }
        return <InnerMore mathStandardSet={mathStandardSet} assessments={assessments} {...props} />
      }}
    />
  )
}

type InnerMorePropsT = {
  courses: CourseAttrs[]
  courseMemberships: CourseMembershipAttrs[]
  schools: SchoolAttrs[]
  currentSchool: SchoolAttrs
  students: StudentAttrs[]
  teachers: BaseTeacherAttrs[]
  mathStandardSet: RlStandardSetT
  assessments: MathSchoolAssessmentAssignmentT[]
  year: number
}

function InnerMore({
  courses,
  courseMemberships,
  schools,
  currentSchool,
  students,
  teachers,
  mathStandardSet,
  assessments,
  year
}: InnerMorePropsT) {
  const domains = useAsync(
    () => fetchRlDomains('math', mathStandardSet, Languages.En),
    'fetchRlDomains',
    ['math', mathStandardSet, Languages.En]
  )
  const standards = useAsync(
    () => fetchRlStandardsWithDomain('math', mathStandardSet.id, Languages.En),
    'fetchRlStandardsWithDomain',
    ['math', mathStandardSet.id, Languages.En]
  )

  return (
    <WithResources
      resources={{domains, standards}}
      render={({domains, standards}) => {
        const studentsInSchool = getAllStudentsBySchool(
          currentSchool.id,
          teachers,
          courses,
          courseMemberships,
          students
        )
        return (
          <Assessments
            assessments={assessments}
            mathStandardSet={mathStandardSet}
            domains={domains}
            standards={standards}
            students={studentsInSchool}
            teachers={teachers}
            schools={schools}
            currentSchool={currentSchool}
            year={year}
          />
        )
      }}
    />
  )
}
